import { authorizedFetch, ErrorResponse } from './api-client';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export type UserRole =
  | 'super_admin'
  | 'company_admin'
  | 'clinic_manager'
  | 'receptionist'
  | 'insurance_billing';

export const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  company_admin: 'Admin da Empresa',
  clinic_manager: 'Gerente de Clínica',
  receptionist: 'Recepcionista',
  insurance_billing: 'Faturamento Convênio',
};

/** Nível hierárquico do role — regra N+1: usuário só cria nível atual + 1. */
export const ROLE_LEVELS: Record<string, number> = {
  super_admin: 0,
  company_admin: 1,
  clinic_manager: 2,
  receptionist: 3,
  insurance_billing: 3,
};

export function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

export interface User {
  id: string;
  name: string;
  email: string;
  cellphone: string;
  role: UserRole | string;
  level?: number;
  company_id?: string | null;
  company_name?: string | null;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
  updated_by?: string;
  status?: number;
}

export interface CreateUserData {
  name: string;
  email: string;
  cellphone: string;
  role: UserRole;
  company_id?: string | null;
}

// O backend (UserUpdateRequest) aceita name/email/cellphone/company_id — role não pode mudar
export interface UpdateUserData {
  name?: string;
  email?: string;
  cellphone?: string;
  company_id?: string | null;
}

export interface PaginatedUsers {
  items: User[];
  total: number;
  page: number;
  page_size: number;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function parseError(response: Response, fallback: string): Promise<ApiError> {
  let detail = fallback;
  try {
    const body: ErrorResponse = await response.json();
    if (body?.detail) detail = body.detail;
  } catch {
    /* mantém fallback */
  }
  return new ApiError(response.status, detail);
}

export async function fetchUsers(
  includeInactive: boolean = false,
  complete: boolean = false,
  nameLike?: string,
  email?: string,
  cellPhoneLike?: string,
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedUsers> {
  const params = new URLSearchParams({
    include_inactive: includeInactive.toString(),
    complete: complete.toString(),
    page: page.toString(),
    page_size: pageSize.toString(),
  });

  if (nameLike) params.append('name_like', nameLike);
  if (email) params.append('email', email);
  if (cellPhoneLike) params.append('cellphone_like', cellPhoneLike);

  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/users/?${params.toString()}`);

  if (!response.ok) {
    throw await parseError(response, 'Erro ao buscar usuários');
  }

  const data = await response.json();
  // Backend retorna sempre PaginatedResponse — mas mantém fallback defensivo
  if (Array.isArray(data)) {
    return { items: data, total: data.length, page, page_size: pageSize };
  }
  return {
    items: data.items ?? [],
    total: data.total ?? 0,
    page: data.page ?? page,
    page_size: data.page_size ?? pageSize,
  };
}

export async function fetchUsersByClinic(clinicId: string): Promise<User[]> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/users/by-clinic/${clinicId}`);
  if (!response.ok) throw new Error('Erro ao buscar usuários da clínica');
  return response.json();
}

export async function createUser(data: CreateUserData): Promise<User> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/users/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw await parseError(response, 'Erro ao criar usuário');
  }

  return response.json();
}

export async function updateUser(id: string, data: UpdateUserData): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw await parseError(response, 'Erro ao atualizar usuário');
  }
}

export async function activateUser(id: string): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/users/${id}/activate`, {
    method: 'PATCH',
  });

  if (!response.ok) {
    throw await parseError(response, 'Erro ao ativar usuário');
  }
}

export async function inactivateUser(id: string): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/users/${id}/inactivate`, {
    method: 'PATCH',
  });

  if (!response.ok) {
    throw await parseError(response, 'Erro ao inativar usuário');
  }
}

export async function deleteUser(id: string): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/users/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw await parseError(response, 'Erro ao deletar usuário');
  }
}
